import { PencilIcon, TrashIcon } from "@heroicons/react/24/outline";

import React, { useEffect, useRef } from "react";
import Loader from "../../components/loader";
import NoData from "../../components/noData";
import { useNCallContext } from "../../context/ncall-context";
import { API } from "../../service/api";
import { shiftTime } from "../../utils/timestamp";
import {
  showErrorToast,
  showInfoToast,
  showSuccessToast,
} from "../../utils/toast";
import Modal from "../../components/modal/modal";
import Table from "../../components/table";
import { useNavigate } from "react-router-dom";
import ConfirmationModal from "../../components/modal/confirmationModal";

// modal config
const config = {
  title: "",
  handler: null,
};

const Shifts = () => {
  const { state, openModal, setOpenModal, getShifts } = useNCallContext();
  const { shifts } = state;
  let _id = "";
  const startTimeRef = useRef();
  const endTimeRef = useRef();
  const confirmationmodalRef = useRef(null);

  const navigate = useNavigate();
  useEffect(() => {
    if (localStorage.getItem("logged-in-as") == "client") {
      navigate("/client");
    }
  }, []);

  const handleUpdateBtnClk = (shift) => {
    startTimeRef.current = shiftTime.from(shift.startTime);
    endTimeRef.current = shiftTime.from(shift.endTime);
    _id = shift.id;
    config.title = "Update Shift";
    config.handler = handleUpdateShift;

    setOpenModal(!openModal);
  };

  const handleUpdateShift = async () => {
    let data = {
      day: "",
      startTime: shiftTime.to(startTimeRef.current.value),
      endTime: shiftTime.to(endTimeRef.current.value),
    };

    if (data.startTime === "Invalid date" || data.endTime === "Invalid date") {
      showInfoToast("Please Select the start and end time for shift");
      return;
    }
    try {
      const res = await API.putShifts(_id, data);
      if (res.error) {
        showErrorToast(res.message);
      } else {
        showSuccessToast(res.message);
      }

      getShifts();
    } catch (error) {
      showErrorToast(error.message);
    }
  };

  const openDeletionConfirmationModal = (shift) => {
    if (confirmationmodalRef) {
      confirmationmodalRef.current.handleModalOpen();
      confirmationmodalRef.current.setRecordForModal(shift);
      confirmationmodalRef.current.setHandlerMethod(() => handleDeleteBtnClk);
    }
  };

  const handleDeleteBtnClk = async (shift) => {
    try {
      const res = await API.deleteShifts(shift.id);
      if (res.error) {
        showErrorToast(res.message);
      } else {
        showSuccessToast(res.message);
      }
      getShifts();
    } catch (error) {
      showErrorToast(error.message);
    }
  };

  const handleAddShift = async () => {
    let data = {
      day: "",
      startTime: shiftTime.to(startTimeRef.current.value),
      endTime: shiftTime.to(endTimeRef.current.value),
    };

    if (data.startTime === "Invalid date" || data.endTime === "Invalid date") {
      showInfoToast("Please Select the start and end time for shift");
      return;
    }
    try {
      const res = await API.postShifts(data);
      if (res.error) {
        showErrorToast(res.message);
      } else {
        showSuccessToast(res.message);
      }

      getShifts();
    } catch (error) {
      showErrorToast(error.message);
    }
  };

  useEffect(() => {
    localStorage.setItem(
      "sort",
      JSON.stringify({ id: "endTime", desc: false })
    );
    getShifts();
  }, []);

  const columns = React.useMemo(
    () => [
      {
        Header: "Start Time",
        accessor: "startTime",
      },
      {
        Header: "End Time",
        accessor: "endTime",
      },
    ],
    []
  );
  const actions = [
    {
      icon: PencilIcon,
      handler: handleUpdateBtnClk,
      styles: "w-5 h-5 text-green-500",
    },
    {
      icon: TrashIcon,
      handler: openDeletionConfirmationModal,
      styles: "w-5 h-5 text-red-500",
    },
  ];

  if (state.loading) return <Loader />;

  return (
    <div className="">
      {<ConfirmationModal ref={confirmationmodalRef} />}
      {/* Modal  */}
      {openModal ? (
        <Modal config={config}>
          <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4  sm:border-gray-200 sm:pt-5">
            <label
              htmlFor="department"
              className="py-4 block text-sm font-medium text-gray-700 sm:mt-px sm:pt-2"
            >
              Start Time
            </label>

            <div className="mt-1 sm:col-span-2 sm:mt-0">
              <input
                ref={startTimeRef}
                required
                defaultValue={startTimeRef.current}
                type="time"
                name="startTime"
                id="startTime"
                className="block w-full max-w-lg rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:max-w-xs sm:text-sm"
              />
            </div>
          </div>
          {/* End Time */}
          <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4  sm:border-gray-200 sm:pt-5">
            <label
              htmlFor="department"
              className="py-4 block text-sm font-medium text-gray-700 sm:mt-px sm:pt-2"
            >
              End Time
            </label>

            <div className="mt-1 sm:col-span-2 sm:mt-0 block w-full max-w-lg rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:max-w-xs sm:text-sm">
              <input
                ref={endTimeRef}
                required
                defaultValue={endTimeRef.current}
                type="time"
                name="endTime"
                id="endTime"
                className="block w-full max-w-lg rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:max-w-xs sm:text-sm"
              />
            </div>
          </div>
        </Modal>
      ) : (
        ""
      )}

      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-base font-semibold leading-6 text-gray-900">
            Shifts
          </h1>
        </div>
        <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
          <button
            onClick={() => {
              config.title = "Add New Shift";
              config.handler = handleAddShift;
              setOpenModal(!openModal);
            }}
            type="button"
            className="block rounded-md bg-indigo-600 py-2 px-3 text-center text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
          >
            Add Shifts
          </button>
        </div>
      </div>
      {!state.loading && shifts.length === 0 ? (
        <NoData message={"You don't have any shifts."} />
      ) : (
        <div className="mt-8 flow-root">
          <div className="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
              <Table
                columns={columns}
                data={state?.shifts}
                actions={actions}
                name="shifts"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Shifts;
